import React, { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { UserPlus } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface InviteUserDialogProps {
  organizationId: string;
  organizationName?: string;
  showSuperAdminOption?: boolean;
  onSuccess?: () => void;
  trigger?: React.ReactNode;
}

const AVAILABLE_ROLES = [
  { value: 'user', label: 'User' },
  { value: 'moderator', label: 'Moderator' },
  { value: 'editor', label: 'Editor' },
  { value: 'admin', label: 'Admin' },
];

export function InviteUserDialog({ 
  organizationId, 
  organizationName, 
  showSuperAdminOption = false,
  onSuccess,
  trigger
}: InviteUserDialogProps) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("user");
  const [makeSuperAdmin, setMakeSuperAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleInvite = async () => {
    if (!email.trim()) {
      toast.error("Please enter an email address");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('invite-user', {
        body: {
          email: email.trim().toLowerCase(),
          organization_id: organizationId,
          role,
          make_super_admin: makeSuperAdmin
        }
      });

      if (error) {
        console.error("Invite error:", error);
        toast.error(error.message || "Failed to send invitation");
        return;
      }

      if (data?.error) {
        toast.error(data.error);
        return;
      }

      toast.success(data?.message || `Invitation sent to ${email}`);
      setOpen(false);
      setEmail("");
      setRole("user");
      setMakeSuperAdmin(false);
      onSuccess?.();
    } catch (err: any) {
      console.error("Invite error:", err);
      toast.error(err.message || "Failed to send invitation");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button size="sm">
            <UserPlus className="h-4 w-4 mr-2" />
            Invite User
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Invite User</DialogTitle>
          <DialogDescription>
            {organizationName 
              ? `Send an invitation to join ${organizationName}`
              : "Send an invitation to join this organization"}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              type="email"
              placeholder="user@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="role">Role</Label>
            <Select value={role} onValueChange={setRole} disabled={isLoading}>
              <SelectTrigger id="role">
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent>
                {AVAILABLE_ROLES.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {role === 'user' && "Can access content and personal dashboard"}
              {role === 'moderator' && "Can view admin dashboard (read-only)"}
              {role === 'editor' && "Can edit content and library resources"}
              {role === 'admin' && "Full access to organization settings"}
            </p>
          </div>
          {showSuperAdminOption && (
            <div className="flex items-center justify-between pt-2 border-t">
              <div>
                <Label htmlFor="super-admin">Make Super Admin</Label>
                <p className="text-xs text-muted-foreground">
                  Platform-level access to all organizations
                </p>
              </div>
              <Switch
                id="super-admin"
                checked={makeSuperAdmin}
                onCheckedChange={setMakeSuperAdmin}
                disabled={isLoading}
              />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleInvite} disabled={isLoading}>
            {isLoading ? "Sending..." : "Send Invitation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
